import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import CallToAction, {
  ICallToActionProps,
} from '../../components/common/blocks/CallToAction';
import FaqCategories, {
  IFaqCategoriesProps,
} from '../../components/common/blocks/FaqCategories';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import Hero, { IHeroComponentProps } from '../../components/home/Hero';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICustomerReviewsMeta,
  IFaqCategory,
  IFaqsPage,
  IFaqSubCategoryLandingPage,
  ILayoutPageProps,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToContactUs,
} from '../../shared/useCommon';
import {
  generateFaqsLandingPageSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
  removeMarkDown,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { NextPageWithLayout } from '../_app';

export interface IFaqsPageProps extends ILayoutPageProps {
  hero: IHeroComponentProps;
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  callToAction: ICallToActionComponent;
  faqCategories: IFaqCategoriesProps;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
}

const FaqsPage: NextPageWithLayout<IFaqsPageProps> = (
  props: IFaqsPageProps
) => {
  const router = useRouter();

  const heroProps: IHeroComponentProps = {
    ...props.hero,
    buttonAction() {
      scrollToContactUs();
    },
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact')
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <meta
          name="facebook-domain-verification"
          content="v1rp4jxjfkqwderk1ntxo8bjfy51hz"
        />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <Hero {...heroProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <FaqCategories {...props.faqCategories} />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...callToActionProps} />
        </div>
      </div>
    </div>
  );
};

FaqsPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: IFaqsPageProps }> {
  const appRoutes = await getRoutes();

  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const pageUrlParams = {
    'populate[0]': 'meta',
    'populate[1]': 'hero',
    'populate[2]': 'hero.background',
    'populate[11]': 'contactUsForm',
    'populate[12]': 'contactUsFormSuccessMessage',
    'populate[13]': 'contactUsFormErrorMessage',
    'populate[14]': 'callToAction',
    'populate[15]': 'callToAction.buttons',
    'populate[16]': 'categoriesTitle',
    'populate[17]': 'faqCategories',
    'populate[18]': 'faqCategories.name',
    'populate[19]': 'faqCategories.subCategoryLandingPath',
    'populate[20]': 'faqCategories.icon',
    'populate[21]': 'faqCategories.faqLandings',
    'populate[22]': 'faqCategories.faqLandings.title',
    'populate[23]': 'faqCategories.faqLandings.metas',
    'populate[24]': 'faqCategories.subCategoryLandingButtonLabel',
  };

  // Get page data
  const page = (
    await fetchStrapi<any>(`/faqs-page`, pageUrlParams, buildApiKey)
  ).data.attributes as IFaqsPage;

  if (!!page.hero && page.hero.length > 0) {
    page.hero.forEach((x) => {
      if (!!(x.background as any).data) {
        x.background = flatStrapiObject(x.background);
      }
    });
  }

  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  // Prepare Hero
  const heroData = getTranslation(page.hero, context.locale);
  const heroProps: IHeroComponentProps = {
    title: heroData.title,
    description: heroData.description,
    buttonLabel: heroData.buttonLabel,
    buttonPath: getLocalizedAppRoute('/countries'),
    backgroundUrl:
      getImageSrcWithFallback(flatStrapiObject(heroData.background), 'full') ||
      '/assets/images/home/map-image.png',
    backgroundAlt:
      flatStrapiObject<any>(heroData.background).alternativeText ||
      'Hero Background Image',
    titleHighlightedText: heroData.highlightedTitleText,
  };

  const meta = getTranslation(page.meta, context.locale);
  const commonTranslations = await getCommonTranslations();
  const contactUsFormSuccessMessage = isUndefinedOrEmpty(
    page.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormSuccessMessage, context.locale)
        .text as string);

  const contactUsFormErrorMessage = isUndefinedOrEmpty(
    page.contactUsFormErrorMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormErrorMessage, context.locale)
        .text as string);

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );

  const contactFormTitle = removeMarkDown(
    getTranslation(page.contactUsForm, context.locale).title as string
  );
  const callToActionTitle = removeMarkDown(
    getTranslation(page.callToAction, context.locale).title as string
  );
  const landingUrlParams = {
    'populate[0]': 'metas',
  };
  const landingsResult = await fetchStrapi<any>(
    `/faq-category-landings`,
    landingUrlParams,
    buildApiKey
  );
  const faqSubCategoryLandings: IFaqSubCategoryLandingPage[] =
    flatStrapiCollection<IFaqSubCategoryLandingPage>(landingsResult);
  const faqsLandingPageSchema = generateFaqsLandingPageSchema(
    meta,
    contactFormTitle,
    callToActionTitle,
    faqSubCategoryLandings,
    getCanonicalLink(context.locale, meta.slug),
    context.locale
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...faqsLandingPageSchema],
  };

  const alternativeLinks = getAlternateLinks('/faqs', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  if (!!page.faqCategories) {
    page.faqCategories = flatStrapiCollection(page.faqCategories as any);

    page.faqCategories.forEach((category: IFaqCategory) => {
      category.icon = flatStrapiObject(category.icon);
      category.faqLandings = flatStrapiCollection(category.faqLandings as any);
    });
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroProps.title;

  const faqCategoriesProps: IFaqCategoriesProps = {
    title: getTranslation(page.categoriesTitle, context.locale).text as string,
    categories: page.faqCategories.map((category: IFaqCategory) => ({
      name: getTranslation(category.name, context.locale).text as string,
      path: getTranslation(category.subCategoryLandingPath, context.locale)
        .text as string,
      buttonLabel: getTranslation(
        category.subCategoryLandingButtonLabel,
        context.locale
      ).text as string,
      iconUrl:
        category.icon?.formats?.large?.url ||
        category.icon?.url ||
        '/assets/images/common/faq-icon.png',
      iconAlt: category.icon?.alternativeText || 'FAQ Category Icon',
      faqs: category.faqLandings.map((faq) => ({
        title: getTranslation(faq.title, context.locale).text as string,
        path: getTranslation(faq.metas, context.locale).slug as string,
      })),
    })),
  };

  const props: IFaqsPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    hero: heroProps,
    contactMethods,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormErrorMessage,
    contactUsFormSuccessMessage,
    callToAction: getTranslation(page.callToAction, context.locale),
    faqCategories: faqCategoriesProps,
    alternativeLinks,
    canonicalLink,
  };

  return {
    props,
  };
}

export default FaqsPage;
